import { constants, metadata } from 'lib_ui-services';

export default {
    verb: 'didCommit',
    namespace: 'item',
    relation: 'item-transaction',
    type: 'create',
    description: 'Notify the user that the commit operation has completed',
    logic: didCommit
};

export const _private = {};

/**
 * @param {{
 *   data: { items: Array, isNew: boolean };
 *   prerequisiteResults: object[];
 *   context: { namespace: string, relation: string };
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 */
async function didCommit({ data, context, dispatch }) {
    if (!(data.message && Array.isArray(data.message.entries) && data.message.entries.length > 0)) {
        return;
    }

    const { namespace, relation } = context;
    const relationName = relation.split('-').shift();
    const prettyName = metadata.getPrettyRelationName(namespace, relationName);

    // A server that applied the batch itself reports what actually happened, per entry (GH #1521).
    // Absent when the client fanned out instead -- an older server, or the batched path switched
    // off -- in which case each entry had its own request with its own commit/rollback and the
    // batch size is all there is to go on here.
    const entryResults = Array.isArray(data.message.entryResults) ? data.message.entryResults : undefined;
    const failed = entryResults?.filter(result => result.status === 'rejected') ?? [];
    // `updated` is the count the server actually applied.  Falling back to `entries.length` -- the
    // batch SIZE -- is what made this message lie: a partial failure, or a total one, still read as
    // "Update completed. N Items updated".
    const count = typeof data.message.updated === 'number' ? data.message.updated : data.message.entries.length;

    if (count > 0) {
        dispatch(
            {
                message: `Update completed. ${count} ${prettyName}${count === 1 ? '' : 's'} updated`,
                timeout: 5000,
                variant: 'success',
                type: constants.notificationTypes.TOAST
            },
            { verb: 'pop', namespace: 'application', relation: 'notification' }
        );
    }

    if (!failed.length) return;

    dispatch(
        {
            isError: true, //todo: gl2025 remove
            variant: 'error',
            type: constants.notificationTypes.TOAST,
            message: failureMessage(failed, prettyName)
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );

    // The failed entries were written optimistically to Loki when the user hit Save, and with the
    // batched path there is no per-entry request left to roll back (the offline outbox never saw
    // one).  Pull the authoritative records back from the server instead of trying to reconstruct
    // the previous values: it reverts the optimistic write and heals any partial state in the same
    // step, and it works even when the commit arrives long after the fact -- offline, or a session
    // later -- when there is no in-memory `oldRecord` left to restore from.
    //
    // `criteria` is a map of NAMED FILTERS, not raw mongo -- `getQueryUrl` looks each key up in the
    // filter factory and silently drops anything it does not recognise, which for a bare `{_id:...}`
    // would mean requesting the relation with no filter at all.  `searchCriteria` is the filter that
    // passes a mongo expression through.  The ids have to be `{$oid}`-wrapped: the server normalizes
    // with `EJSON.parse`, which turns `{$oid: hex}` into an ObjectId but leaves a bare hex string a
    // string, and a string never matches an ObjectId `_id`.
    await dispatch(
        {
            query: {
                criteria: {
                    searchCriteria: { _id: { $in: failed.map(result => ({ $oid: result._id })) } }
                }
            }
        },
        { verb: 'getFromServer', namespace, relation: relationName }
    );
}

/**
 * Name the entries that failed rather than only counting them -- the user selected these items
 * deliberately and needs to know which ones to look at.  Capped so a batch that fails wholesale
 * does not produce an unreadable toast.
 */
function failureMessage(failed, prettyName) {
    const MAX_NAMED = 3;
    const names = failed
        .map(result => result.title)
        .filter(Boolean)
        .slice(0, MAX_NAMED);
    const suffix = `${failed.length} ${prettyName}${failed.length === 1 ? '' : 's'} could not be updated`;
    if (!names.length) return `${suffix}.`;
    const remainder = failed.length - names.length;
    return remainder > 0
        ? `${suffix}: ${names.join(', ')} and ${remainder} more.`
        : `${suffix}: ${names.join(', ')}.`;
}
